import React, { useState, useEffect, useMemo } from "react";
import { Search, Filter, UserCheck, Mail, Phone, Calendar } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import moment from 'moment';
import Sidebar1 from "components/Sidebar1";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { fetchWithToken } from "api/ApiHandler";
import DEfulatImg from '../../Assests/Images/DefulatLogo.png';

const getAvatarUrl = (user) => {
  const path = user?.imagePath || user?.image;
  if (!path || path === "image") return DEfulatImg;
  return path;
};

const getFullName = (user) => `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Unknown";

const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  const m = moment.utc(value);
  if (!m.isValid()) return "-";
  return m.format(withTime ? "DD/MM/YYYY hh:mm A" : "DD/MM/YYYY");
};

const handleAuthError = (error) => {
  const status = error?.response?.status;
  const msg = error?.response?.data?.msg;
  if (status === 401 || msg === "Invalid Token") {
    localStorage.clear();
    window.location.href = "/admin-login";
    return true;
  }
  return false;
};

export default function UserManagement() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All"); // All | Subscribed | Not Subscribed
  const [selectedUser, setSelectedUser] = useState(null);
  const [visible, setVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const loadCustomers = async () => {
    try {
      setIsLoading(true);
      setLoadError("");
      const data = await fetchWithToken("/admin/get-all-customers");
      setCustomers(Array.isArray(data?.customers) ? data.customers : []);
    } catch (error) {
      console.error("Fetch customers error:", error.response?.data || error);
      if (handleAuthError(error)) return;
      setLoadError("Unable to load customers.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Search & Filter Logic
  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return customers.filter((user) => {
      const matchesSearch =
        getFullName(user).toLowerCase().includes(term) ||
        (user.email || "").toLowerCase().includes(term);
      const matchesStatus =
        statusFilter === "All" ||
        (statusFilter === "Subscribed" && user.subscribed) ||
        (statusFilter === "Not Subscribed" && !user.subscribed);
      return matchesSearch && matchesStatus;
    });
  }, [customers, searchTerm, statusFilter]);

  // --- PAGINATION LOGIC ---
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const recentCustomers = useMemo(() => {
    return [...customers]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5);
  }, [customers]);

  const toggleFilter = () => {
    const statuses = ["All", "Subscribed", "Not Subscribed"];
    const currentIndex = statuses.indexOf(statusFilter);
    setStatusFilter(statuses[(currentIndex + 1) % statuses.length]);
  };

  return (
    <div className="flex h-screen bg-[#F9FAFB] overflow-hidden font-sans">

      {/* 1. Integrated Sidebar */}
      <Sidebar1 isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />

      {/* Main Content Area */}
      <main className="flex-1 p-5 overflow-y-auto" style={{paddingLeft:10}}>
        <header className="flex items-center justify-between p-4 lg:px-5" style={{paddingLeft:10}}>
          <h1 style={{fontFamily:'NeueMontreal'}} className="text-2xl lg:text-3xl font-medium text-gray-900">
           User Management (Customers)
          </h1>
          <div className="flex items-center gap-4">

             {/* <div className="w-10 h-10 rounded-xl overflow-hidden border shadow-sm">
                <img src="https://i.pravatar.cc/150?u=admin" alt="Admin" className="w-full h-full object-cover" />
             </div> */}
          </div>
        </header>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto  pt-0 pl-5 pr-5">
          <div className="grid grid-cols-[65%_calc(35%-2rem)] md:grid-cols-1 lg:grid-cols-[65%_calc(35%-2rem)] gap-5">

            {/* Grid Section */}
            <div style={{background:'white',boxShadow:'0px 0px 3px 0px #c3c3c3d1'}} className="xl:col-span-2 bg-white p-5 lg:p-8 rounded-[32px] border border-gray-50 shadow-sm self-start">

              <div className="flex items-center justify-between mb-8 gap-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <InputText
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name or email"
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-1 focus:ring-red-400"
                  />
                </div>
                <Button
                  icon={<Filter size={18} className={statusFilter !== 'All' ? 'text-white' : 'text-[#EA352B]'} />}
                  onClick={toggleFilter}
                  className={`p-2.5 rounded-xl border transition-all ${statusFilter !== 'All' ? 'bg-red-500' : 'bg-white'}`}
                  tooltip={`Current: ${statusFilter}`}
                />
              </div>

              <div className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-1 lg:grid-cols-3 gap-4">
                {isLoading ? (
                  Array.from({ length: 9 }).map((_, i) => <CustomerCardSkeleton key={i} />)
                ) : loadError ? (
                  <p className="col-span-full text-center text-sm text-red-400 py-10">{loadError}</p>
                ) : currentItems.length === 0 ? (
                  <p className="col-span-full text-center text-sm text-gray-400 py-10">No customers found.</p>
                ) : currentItems.map((user) => (
                  <div style={{boxShadow:"rgb(172 168 168 / 82%) 0px 0px 2px 0px"}}
                    key={user._id}
                    onClick={() => { setSelectedUser(user); setVisible(true); }}
                    className="bg-[#F6F6F6] p-4 rounded-[20px] border border-transparent hover:border-red-100 hover:shadow-lg transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <img src={getAvatarUrl(user)} onError={(e) => { e.currentTarget.src = DEfulatImg; }} className="w-10 h-10 rounded-lg object-cover" alt="" />
                      <div className="min-w-0">
                        <p style={{fontFamily:'NeueMontreal'}} className="text-[14px] font-medium text-black group-hover:text-red-600 truncate">{getFullName(user)}</p>
                        <p style={{fontFamily:'NeueMontreal'}} className="text-[12px] text-black font-light">Joined {formatDate(user.createdAt)}</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200/50">
                      <span style={{fontFamily:'NeueMontreal'}} className="text-[11px] text-[black] font-light">Subscription</span>
                      <span style={{fontFamily:'NeueMontreal'}} className={`text-[14px] font-medium ${user.subscribed ? 'text-green-500' : 'text-gray-400'}`}>
                        {user.subscribed ? 'Subscribed' : 'Not Subscribed'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* --- PAGINATION SECTION --- */}
              {totalPages > 1 && (
                <div className="flex justify-end mt-10 gap-2 items-center">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => paginate(currentPage - 1)}
                      className={`text-xs font-medium px-2 py-1 transition ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-black'}`}
                    >
                      ← Previous
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i + 1}
                        style={{fontFamily:"'NeueMontreal', sans-serif",color:currentPage === i + 1 ? 'white' : 'black'}}
                        onClick={() => paginate(i + 1)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition ${
                          currentPage === i + 1
                          ? 'bg-[#EA352B] text-white shadow-lg font-bold'
                          : 'hover:bg-gray-100 text-gray-500'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}

                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => paginate(currentPage + 1)}
                      className={`text-xs font-bold px-2 py-1 transition ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-black'}`}
                    >
                      Next →
                    </button>
                </div>
              )}
            </div>

            {/* Recently Joined Sidebar */}
            <div style={{background:'white',boxShadow:'0px 0px 3px 0px #c3c3c3d1'}} className="bg-white p-6 rounded-[32px] border border-gray-50 shadow-sm h-fit">
              <div className="flex items-center gap-2 mb-6">
                <div className="p-1.5 bg-red-50 rounded-lg"><UserCheck size={16} className="text-[#EA352B]" /></div>
                <h3 style={{fontFamily:'NeueMontreal'}} className="text-[20px] font-medium text-black">Recently Joined</h3>
              </div>
              <div className="space-y-4">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => <RecentCustomerSkeleton key={i} />)
                ) : recentCustomers.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No customers yet.</p>
                ) : recentCustomers.map((customer) => (
                  <div key={customer._id} style={{boxShadow:'rgb(172 168 168 / 82%) 0px 0px 2px 0px'}} className="bg-[#F9FAFB] p-3 rounded-2xl flex items-center justify-between group">
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={getAvatarUrl(customer)} onError={(e) => { e.currentTarget.src = DEfulatImg; }} className="w-10 h-10 rounded-lg object-cover shrink-0" alt="" />
                      <div className="min-w-0">
                        <p style={{fontFamily:'NeueMontreal'}} className="text-[14px] font-medium text-black truncate">{getFullName(customer)}</p>
                        <p style={{fontFamily:'NeueMontreal'}} className="text-[11px] text-black font-light">{formatDate(customer.createdAt)}</p>
                      </div>
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-1 rounded-full shrink-0 ${customer.subscribed ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                      {customer.subscribed ? 'Subscribed' : 'Free'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Details Modal */}
      <Dialog
        header="Customer Profile" visible={visible} onHide={() => setVisible(false)}
        className="w-[50vw] md:w-[450px]" draggable={false}
      >
        {selectedUser && (
          <div className="text-center">
            <img src={getAvatarUrl(selectedUser)} onError={(e) => { e.currentTarget.src = DEfulatImg; }} className="w-24 h-24 rounded-2xl mx-auto mb-4 border-4 border-red-50 object-cover" alt="" />
            <h2 className="text-xl font-bold">{getFullName(selectedUser)}</h2>
            <Tag value={selectedUser.subscribed ? 'Subscribed' : 'Not Subscribed'} severity={selectedUser.subscribed ? 'success' : 'secondary'} className="mt-1" />

            <div className="mt-8 text-left space-y-4 border-t pt-6">
              <div className="flex gap-4"><Mail className="text-gray-400" size={18}/> <div><p className="text-[10px] text-gray-400 font-bold uppercase">Email</p><p className="text-sm">{selectedUser.email || "-"}</p></div></div>
              <div className="flex gap-4"><Phone className="text-gray-400" size={18}/> <div><p className="text-[10px] text-gray-400 font-bold uppercase">Phone</p><p className="text-sm">{selectedUser.phNumber || "-"}</p></div></div>
              <div className="flex gap-4"><Calendar className="text-gray-400" size={18}/> <div><p className="text-[10px] text-gray-400 font-bold uppercase">Joined</p><p className="text-sm">{formatDate(selectedUser.createdAt, true)}</p></div></div>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}

function CustomerCardSkeleton() {
  return (
    <div className="bg-[#F6F6F6] p-4 rounded-[20px]">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse" />
        <div className="flex-1">
          <div className="h-3 w-24 bg-gray-200 rounded animate-pulse mb-2" />
          <div className="h-2 w-20 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
      <div className="flex justify-between items-center pt-2 border-t border-gray-200/50">
        <div className="h-2 w-14 bg-gray-200 rounded animate-pulse" />
        <div className="h-2 w-16 bg-gray-200 rounded animate-pulse" />
      </div>
    </div>
  );
}

function RecentCustomerSkeleton() {
  return (
    <div className="bg-[#F9FAFB] p-3 rounded-2xl flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse shrink-0" />
      <div className="flex-1">
        <div className="h-3 w-24 bg-gray-200 rounded animate-pulse mb-2" />
        <div className="h-2 w-16 bg-gray-200 rounded animate-pulse" />
      </div>
    </div>
  );
}
